import type { LexicalText } from "@/schema"

import type { EditorRefHandle } from "@/components/lexical/editors/email"

export const EMAIL_EDITOR_SYNC_ERROR =
  "We couldn't safely read your latest edits, so the email was not submitted. Copy the email body, close and reopen the composer, then paste it before trying again."

type OutgoingContentResult =
  | { ok: true; content: LexicalText }
  | { ok: false; reason: "editor_unavailable" | "editor_faulted" | "content_unavailable" | "content_read_failed" }

type OutgoingContentEditor = Pick<EditorRefHandle, "getContent" | "hasFaulted">

const NON_TEXT_CONTENT = /<(?:audio|hr|iframe|img|svg|table|video)\b/i
const INVISIBLE_HTML = /(?:&nbsp;|&#0*160;|&#x0*a0;|&ZeroWidthSpace;|\u00a0|\u200b)/gi

export function hasVisibleOutgoingContent(content?: LexicalText): boolean {
  const html = content?.html?.trim()
  if (!html) return false
  if (NON_TEXT_CONTENT.test(html)) return true

  return (
    html
      .replace(/<[^>]*>/g, "")
      .replace(INVISIBLE_HTML, "")
      .trim().length > 0
  )
}

/**
 * Read the outgoing body directly from the mounted editor at submit time.
 *
 * The editor can update ahead of the composer store, so falling back to stored
 * content risks sending a body that no longer matches what the user sees.
 */
export function readOutgoingContent(editor: OutgoingContentEditor | null): OutgoingContentResult {
  if (!editor) return { ok: false, reason: "editor_unavailable" }

  try {
    if (editor.hasFaulted()) return { ok: false, reason: "editor_faulted" }

    const content = editor.getContent()
    if (!content) return { ok: false, reason: "content_unavailable" }

    return { ok: true, content }
  } catch {
    return { ok: false, reason: "content_read_failed" }
  }
}
